/**
 * The periodic push-integrity check.
 *
 * `DatabaseWrapper` holds two different things at once: a replication engine
 * that keeps this device's local PouchDB in step with the remote CouchDB, and a
 * document-access API (get / save / query) that the rest of the app calls. This
 * file holds one piece of the first one — the check that runs on resume, on
 * coming back online and on the foreground health tick, samples the few local
 * documents that changed most recently, asks the server whether it has the same
 * revision of each, and writes any document the server is missing or behind on
 * straight to the server.
 *
 * Like `database-wrapper-sync-start.ts`, `database-wrapper-pull-catchup.ts` and
 * `database-wrapper-initial-sync.ts`, it deliberately knows nothing about
 * `DatabaseWrapper`. Everything it touches arrives through the
 * `PushIntegrityContext` object below, which is why the module can be exercised
 * in a unit test with a small fake instead of a whole database wrapper — the
 * class itself imports `pouchdb-browser`, which reads `self` at module scope and
 * cannot even be LOADED outside a browser, so before this move the only way to
 * protect the rules below was to read this function's own source text and
 * pattern-match on it.
 *
 * Nothing here changed behaviour when it moved out of `database-wrapper.ts`.
 * The rules below were already true and are restated because they are easy to
 * break with an edit that looks harmless:
 *
 *  - **Recovery goes through `forcePushDocAsync`, never PouchDB's replication
 *    machinery** (`replicate.to`, `checkpoint: false`). Three such
 *    configurations moved zero documents when this was built. See
 *    `design-docs/sync-integrity.md`, "Why PouchDB Replication Doesn't Work for
 *    Push Recovery".
 *  - **The settle guard skips WITHOUT writing the cooldown** (Sentry 3ZD
 *    race-409 fix). If live sync was active a moment ago, a push may still be in
 *    flight; running now would report ordinary replication lag as drift and race
 *    that push into a 409. Not writing the cooldown is what lets the next resume
 *    retry. The skips are capped so a busy but healthy sync cannot starve the
 *    check for ever.
 *  - **Device-local documents are never sampled** (#4219). They never sync, so
 *    their local revision is permanently ahead of the orphaned server copy, and
 *    sampling them made this check push them to the server every few minutes.
 *  - **A same-generation, different-hash revision counts as drift** (#3987 A4).
 *    It means local and remote hold divergent leaves; the force-push merges a
 *    diverged remote body rather than overwriting it, so recovering it is safe.
 *  - **The Mixpanel volume metric is unconditional; the Sentry report is not**
 *    (Fix D). A failed recovery is reported to Sentry only on a healthy,
 *    foregrounded connection — offline, hidden, or a fresh network-shaped sync
 *    error means the document is safe locally and will flush on the next sync.
 *  - **Every completed pass reconciles the healed-on-retry state** (#4010 B4) —
 *    the recovery path, the no-drift return and the nothing-to-sample return. A
 *    path that forgets it silently stops the `sync_drift_healed_on_retry` report.
 *  - **The cooldown is written when the check itself completed**, including
 *    when some recoveries failed. Only a thrown exception skips it, so the next
 *    attempt is not suppressed.
 */

import { DateTime } from 'luxon';
import { WhenMomentUnreadable, mayHappenAgainSinceStored } from '@cf-shared/periodic-suppression';
import { ForcePushTrigger, ForcePushResult } from './database-wrapper-force-push';
import { isDatabaseTornDown } from './database-unavailable-error';
import { errorReportFields } from './error-patterns';
import { LastSyncError, SyncFailureClass, classifySyncFailure } from './sync-failure-classification';
import { computeDriftRetryOutcome, isSyncDiagnosticEnvironmentHealthy } from './sync-health-decisions';
import { parseRevGeneration } from './sync-rev-and-time-utils';
import { docTypeFromId } from './sync-telemetry-shaping';

/** how long after the last live-sync activity the check waits before it runs */
export const CHECKPOINT_SETTLE_MS = 5_000;
/** how many times in a row the settle guard may defer the check */
export const CHECKPOINT_SETTLE_MAX_SKIPS = 3;
/** sessionStorage key holding when the check last completed */
export const CHECKPOINT_INTEGRITY_COOLDOWN_KEY = 'lastCheckpointIntegrityCheck_utc';
/**
 * sessionStorage key present while a thrown check has been reported and no check has completed
 * since. The failing path skips the cooldown on purpose, so an always-failing check runs on every
 * resume and health tick; this mark keeps it to one report per outage (#5191).
 */
export const INTEGRITY_CHECK_FAILURE_REPORTED_KEY = 'checkpointIntegrityCheckFailureReported';

/**
 * Everything the push-integrity check needs from the database wrapper around it.
 *
 * Grouped on purpose, and a new member goes in the group it belongs to: state
 * this module only reads, state it also writes back, and things it asks the
 * wrapper to do. A member here is not an invitation to add more — each one is a
 * piece of the document-access side that the sync engine has not been separated
 * from yet. See deferred finding `df-3c79746add23`.
 */
export interface PushIntegrityContext {
  // ---- state this module only reads ----

  readonly localDb: any;
  readonly remoteDb: any;
  /** the name used in log lines, e.g. `user` */
  readonly internalDatabaseIdentifier: string;
  /**
   * True when the database's state is `DatabaseState.Ready`. Handed over as a
   * boolean so this module does not have to import that enum, which lives in
   * `database-wrapper.ts` and cannot be loaded outside a browser.
   */
  readonly databaseIsReady: boolean;
  /** epoch ms of the most recent live-sync activity; 0 when there has been none */
  readonly lastSyncActivityTime_ms: number;
  /** true when the connection state is `ConnectionState.Online` */
  readonly connectionStateIsOnline: boolean;
  /** the connection state's own value, published verbatim in the Sentry context */
  readonly connectionState: string;
  /** the most recent live-sync error, published verbatim in the Sentry context */
  readonly lastSyncError: LastSyncError | null;
  /** documents that never leave this device; never sampled here (#4219) */
  readonly deviceLocalDocIds: readonly string[];
  readonly telemetry: { trackEvent(name: string, params: { [key: string]: any }): void } | null;
  readonly logging: {
    trackSilentProblem(name: string, context: { [key: string]: any }): void;
  };

  // ---- state this module also writes back ----

  /** consecutive settle-guard deferrals; reset once the check gets past the guard */
  checkpointSettleSkips: number;

  // ---- things it asks the wrapper to do ----

  isUserDb(): boolean;
  forcePushDocAsync(docId: string, trigger: ForcePushTrigger): Promise<ForcePushResult>;
  /** true when the live sync failed with a network-shaped error in the last 30 s */
  hasRecentNetworkSyncError(): boolean;
}

/** one sampled document whose server copy is missing, behind, or divergent */
interface DriftedDoc {
  docId: string;
  localRev: string;
  remoteRev: string | null;
  sameGen?: boolean;
}

/**
 * Periodic integrity check: samples recent local docs and verifies they exist
 * on CouchDB with the same (or newer) revision. If drift is detected (local rev
 * is ahead of remote), pushes drifted docs directly via forcePushDocAsync.
 *
 * IMPORTANT: Do NOT hand the recovery to PouchDB's replication machinery
 * (`replicate.to`, `checkpoint: false`) — three such configurations moved zero
 * documents when this was built. Always use forcePushDocAsync, which reads the
 * document locally and writes it straight to the remote.
 *
 * The reason once given here — "revision tree format incompatibilities" with
 * the custom SQLite adapter — was withdrawn in #4407 Step 5 as untrue: the live
 * sync's own push half is `replicate.to` on that adapter and it works in
 * production every day. The observation stands, the explanation does not. See
 * `design-docs/sync-integrity.md`, "Why PouchDB Replication Doesn't Work for
 * Push Recovery", and note the rule is about `replicate.to` specifically —
 * forcePushDocAsync itself now uses `bulkDocs({ new_edits: false })` on its
 * main path, deliberately.
 */
export async function verifyPushCheckpointIntegrityInternalAsync(ctx: PushIntegrityContext): Promise<void> {
  // guard: only user DB, only when sync is idle (Ready)
  if (!ctx.isUserDb()) return;
  if (!ctx.localDb || !ctx.remoteDb) return;
  if (!ctx.databaseIsReady) return;

  // settle guard: if live sync was active very recently, a push may still be in flight —
  // running now would flag "drift" that is just normal replication lag and would race the
  // push, producing a force-push 409 (Sentry 3ZD race-409). skip WITHOUT writing the
  // cooldown so the next resume/online event retries. capped so a continuously-active
  // healthy sync cannot starve the check forever.
  const sinceSyncActivityMs = DateTime.utc().toMillis() - ctx.lastSyncActivityTime_ms;
  if (ctx.lastSyncActivityTime_ms > 0 && sinceSyncActivityMs < CHECKPOINT_SETTLE_MS
      && ctx.checkpointSettleSkips < CHECKPOINT_SETTLE_MAX_SKIPS) {
    ctx.checkpointSettleSkips++;
    console.log(`Db:${ctx.internalDatabaseIdentifier}: checkpoint integrity check deferred — sync active ${sinceSyncActivityMs}ms ago (skip ${ctx.checkpointSettleSkips})`);
    return;
  }
  ctx.checkpointSettleSkips = 0;

  // cooldown: max once per 5 minutes. An unreadable stored time checks now
  // rather than holding back — `Allow` — because skipping would suppress a
  // health check on exactly the device whose stored state is already damaged.
  const cooldownKey = CHECKPOINT_INTEGRITY_COOLDOWN_KEY;
  if (!mayHappenAgainSinceStored(
    sessionStorage[cooldownKey],
    { minutes: 5 },
    DateTime.utc(),
    WhenMomentUnreadable.Allow,
  )) {
    return;
  }

  const logHeader = `Db:${ctx.internalDatabaseIdentifier}:`;

  try {
    // get 5 most recently modified local docs
    // over-fetch (limit: 20) because SQLite adapter skips non-winning revisions
    const changes = await ctx.localDb.changes({
      descending: true,
      limit: 20,
      include_docs: false,
      since: 0,
    });

    const seen = new Set<string>();
    const localDocs: { id: string; rev: string }[] = [];
    for (const row of changes.results) {
      if (seen.has(row.id)) continue;
      // skip internal/non-user docs
      if (row.id.startsWith('_design/') || row.id.startsWith('_local/')
          || row.id.startsWith('config/') || row.id.startsWith('mix/')) continue;
      // skip device-local docs: they never sync, so their local rev is
      // permanently "ahead" of the orphaned server copy — sampling them here
      // made the recovery force-push the doc to the server every few
      // minutes, bypassing the replication filters (#4219 leak)
      if (ctx.deviceLocalDocIds.includes(row.id)) continue;
      if (row.deleted) continue;
      seen.add(row.id);
      localDocs.push({ id: row.id, rev: row.changes[0].rev });
      if (localDocs.length >= 5) break;
    }

    if (localDocs.length === 0) {
      // #4010 B4: even a pass with nothing to sample reconciles, so previously-failed docs
      // are not stuck unreported until a normal pass runs (same absence-means-resolved
      // approximation the no-drift path uses — acceptable for a volume metric)
      reconcileDriftRetryOutcome(ctx, []);
      endIntegrityCheckFailureOutage();
      return;
    }

    // compare against CouchDB
    const remoteResult = await ctx.remoteDb.allDocs({
      keys: localDocs.map(d => d.id),
      include_docs: false,
    });

    const driftedDocs: DriftedDoc[] = [];
    for (let i = 0; i < localDocs.length; i++) {
      const local = localDocs[i];
      const remote = remoteResult.rows[i];
      const remoteRev = remote?.value?.rev ?? null;

      if (!remoteRev || remote.error) {
        // doc missing on remote or allDocs returned an error for this key
        driftedDocs.push({ docId: local.id, localRev: local.rev, remoteRev: null });
      } else {
        // parseRevGeneration falls back to 0 for a malformed rev, so a bad rev can't
        // silently evaluate to NaN and skip the drift comparison
        const localGen = parseRevGeneration(local.rev);
        const remoteGen = parseRevGeneration(remoteRev);
        if (localGen > remoteGen) {
          driftedDocs.push({ docId: local.id, localRev: local.rev, remoteRev });
        } else if (localGen === remoteGen && local.rev !== remoteRev) {
          // same generation but different hash: local and remote hold divergent leaves
          // (#3987 A4, defect D6 — invisible to the old generation-only comparison).
          // Recovery is the same force-push below, which since A3 merges a diverged
          // remote body instead of clobbering it, so recovering these is safe.
          driftedDocs.push({ docId: local.id, localRev: local.rev, remoteRev, sameGen: true });
        }
      }
    }

    if (driftedDocs.length === 0) {
      console.log(`${logHeader} Checkpoint integrity OK (${localDocs.length} docs verified)`);
      // #4010 B4: docs that failed healing on an earlier pass and no longer drift at all
      // count as healed — reconcile so the healed-on-retry follow-up still fires here
      reconcileDriftRetryOutcome(ctx, []);
      endIntegrityCheckFailureOutage();
      sessionStorage[cooldownKey] = DateTime.utc().toISO();
      return;
    }

    // drift detected — log locally, fire Mixpanel volume metric, attempt recovery
    console.warn(`${logHeader} Checkpoint drift detected:`, driftedDocs);

    // shared derivation — see docTypeFromId (a last-dash split misreads
    // a suffix-less log id and buckets every non-log doc by a uuid fragment)
    const docTypes = [...new Set(driftedDocs.map(d => docTypeFromId(d.docId)))];
    ctx.telemetry?.trackEvent('sync_checkpoint_drift', {
      drifted_doc_count: driftedDocs.length,
      doc_types: docTypes.join(','),
      // #3987 A4: how many of the drifted docs are same-generation divergences,
      // so the new detections are distinguishable from classic drift in Mixpanel
      same_gen_count: driftedDocs.filter(d => d.sameGen).length,
    });

    // recovery: direct PUT for each drifted doc (NOT PouchDB replication — see Context)
    console.log(`${logHeader} Recovering ${driftedDocs.length} drifted docs via direct PUT`);
    // collect docs whose direct PUT failed; the Sentry alert is gated on this list.
    // each failure carries WHY it failed (#4010 B4): 'network' failures corroborate the
    // residual-is-transient theory, 'http-<status>' points at a server-side cause.
    // a 409 whose re-check also failed carries that failure too (#5191)
    const failedDocs: (DriftedDoc & { failureClass?: SyncFailureClass, recheckFailureClass?: SyncFailureClass })[] = [];
    for (const drifted of driftedDocs) {
      const pushResult = await ctx.forcePushDocAsync(drifted.docId, 'drift-recovery');
      if (!pushResult.pushed) {
        failedDocs.push({ ...drifted, failureClass: pushResult.failureClass, recheckFailureClass: pushResult.recheckFailureClass });
      }
    }

    // #4010 B4: report docs that failed on an EARLIER pass and are no longer failing —
    // the "healed on retry after N minutes" follow-up that turns the transient theory
    // into measured fact — and persist this pass's failures for the next comparison
    reconcileDriftRetryOutcome(ctx, failedDocs.map(f => f.docId));
    endIntegrityCheckFailureOutage();

    // recovery failed for some docs — but only report to Sentry when it looks like a
    // genuine, non-recoverable stall. A transient network drop (connectionState offline or
    // a fresh network-shaped sync error) or a backgrounded app means the doc is safe
    // locally and will flush on the next sync, not a data-loss risk (Sentry 3ZD
    // false-positive fix). Fix C already drops race-409s — where normal replication
    // already delivered the doc — from failedDocs. The Mixpanel volume metric above stays
    // unconditional, so drift visibility is unchanged.
    // the database term is left out: this check only runs once the database is Ready
    const environmentHealthy = isSyncDiagnosticEnvironmentHealthy({
      connectionStateIsOnline: ctx.connectionStateIsOnline,
      documentHidden: document.hidden,
      recentNetworkSyncError: ctx.hasRecentNetworkSyncError(),
    });
    if (failedDocs.length > 0 && environmentHealthy) {
      // actionable signal — recovery failed on a healthy, foregrounded connection
      ctx.logging.trackSilentProblem('SyncCheckpointDrift', {
        driftedDocs,
        failedDocs,
        // #4010 B4: the distinct failure classes, hoisted for at-a-glance triage
        failureClasses: [...new Set(failedDocs.map(f => f.failureClass ?? 'other'))].join(','),
        connectionState: ctx.connectionState,
        documentHidden: document.hidden,
        lastSyncError: ctx.lastSyncError,
        timeSinceLastSync_ms: sessionStorage.lastUserDbSyncTime_utc
          ? DateTime.utc().diff(DateTime.fromISO(sessionStorage.lastUserDbSyncTime_utc)).as('milliseconds')
          : null,
      });
    } else if (failedDocs.length > 0) {
      console.log(`${logHeader} Checkpoint drift recovery failed for ${failedDocs.length} doc(s), but a transient network/background state explains it — no Sentry alert`);
    } else {
      console.log(`${logHeader} Checkpoint drift recovery completed — no Sentry alert`);
    }

    // write cooldown when the integrity check itself completed (per-doc recovery
    // failures are reported via Sentry above; only a thrown exception in the outer
    // try should skip cooldown so the next check attempt isn't suppressed)
    sessionStorage[cooldownKey] = DateTime.utc().toISO();
  } catch (err) {
    console.log(`${logHeader} Checkpoint integrity check failed:`, err);
    reportIntegrityCheckFailure(ctx, err);
  }
}

/**
 * Report a check that threw, unless the failure explains itself (#5191). Quiet in the same
 * situations the SyncCheckpointDrift report is quiet — offline, backgrounded, or just after a
 * network-shaped sync error — and also when this error is itself network-shaped, or the database
 * stopped being Ready while the check ran (sign-out or recovery closing it, which surfaces as a
 * raw PouchDB error rather than a DatabaseUnavailableError): the next sync or app start retries. What is left is a
 * check that fails on a healthy connection, which leaves drift unwatched until someone looks.
 */
function reportIntegrityCheckFailure(ctx: PushIntegrityContext, err: unknown): void {
  const failureClass = classifySyncFailure(err as any);
  if (failureClass === 'network' || isDatabaseTornDown(err)) return;
  // if the database went away mid-check, this is our own teardown — the getter is live
  if (!ctx.databaseIsReady) return;
  const environmentHealthy = isSyncDiagnosticEnvironmentHealthy({
    connectionStateIsOnline: ctx.connectionStateIsOnline,
    documentHidden: document.hidden,
    recentNetworkSyncError: ctx.hasRecentNetworkSyncError(),
  });
  if (!environmentHealthy) return;
  // if this outage was already reported, do not report it again until a check completes
  if (sessionStorage[INTEGRITY_CHECK_FAILURE_REPORTED_KEY]) return;
  sessionStorage[INTEGRITY_CHECK_FAILURE_REPORTED_KEY] = 'true';
  ctx.logging.trackSilentProblem('SyncIntegrityCheckFailed', {
    ...errorReportFields(err),
    failureClass,
    connectionState: ctx.connectionState,
    lastSyncError: ctx.lastSyncError,
  });
}

/** A check that got to the end ends any outage reported above, so the next failure is reported. */
function endIntegrityCheckFailureOutage(): void {
  delete sessionStorage[INTEGRITY_CHECK_FAILURE_REPORTED_KEY];
}

/**
 * #4010 B4: compare this integrity pass's failed heal docs with the previous pass's
 * (persisted in sessionStorage) and report the ones that healed on retry — the follow-up
 * that turns "the 3ZD residual is transient and self-heals next tick" from belief into
 * measured fact. Called from ALL THREE completion paths of the check: after the recovery
 * loop, on the no-drift return and on the nothing-to-sample return (a previously-failed
 * doc that no longer drifts healed too).
 */
function reconcileDriftRetryOutcome(ctx: PushIntegrityContext, currentFailedIds: string[]): void {
  const outcome = computeDriftRetryOutcome(
    sessionStorage.driftFailedDocs,
    currentFailedIds,
    DateTime.utc().toMillis(),
    DateTime.utc().toISO(),
  );
  if (outcome.healed.length > 0) {
    console.log(`Db:${ctx.internalDatabaseIdentifier}: ${outcome.healed.length} drift doc(s) healed on retry`, outcome.healed);
    ctx.telemetry?.trackEvent('sync_drift_healed_on_retry', {
      healed_count: outcome.healed.length,
      max_retry_after_ms: Math.round(Math.max(...outcome.healed.map(h => h.retryAfterMs))),
    });
  }
  sessionStorage.driftFailedDocs = outcome.nextFailedJson;
}
